<template>
  <o-modal :active="true" :canCancel="false" class="modal-xs">
    <div class="modal-card">

      <!-- Header -->
      <header class="modal-card-head">
        <h4 class="modal-card-title">
          {{ isEdit ? `Update ${config.label}` : `Add a New ${config.label}` }}
          <span class="line-height required-label is-size-5 is-italic is-pulled-right mr-0 has-text-weight-normal">
            * required
          </span>
        </h4>
      </header>

      <!-- Body -->
      <div class="modal-card-body is-visible px-0 pt-0">
        <div class="column">
          <div class="columns" v-if="item.cardType !== 'templateForm' && item.formType !== 'template'">
            <div class="column">
              <h3 class="is-size-3 has-text-black-bis has-text-weight-semibold line-height">
                {{ config.label }} Name <span class="has-text-danger">*</span>
              </h3>
               <input
                  class="input"
                  type="text"
                  v-model.trim="form.name"
                  :placeholder="`Enter a ${config.label} name (3 or more characters)`"
                />
            </div>
          </div>
          <div class="field" v-if="item.cardType === 'templateForm'">
          <div class="field-label">
            <label class="label is-size-3">Template Name</label>
          </div>
          <div class="field-body">
            <div class="control">
              <input
                class="input"
                type="text"
                v-model.trim="form.templateName"
              />
            </div>
          </div>
        </div>

        <!-- Template ID -->
        <div class="field" v-if="item.cardType === 'templateForm'">
          <div class="field-label">
            <label class="label is-size-3">Template ID</label>
          </div>
          <div class="field-body">
            <div class="control">
              <input
                class="input"
                type="text"
                v-model.trim="form.templateId"
              />
            </div>
          </div>
        </div>

        <div class="field" v-if="item.cardType === 'templateForm'">
          <div class="field-label">
            <label class="label is-size-3">Default Template</label>
          </div>
          <div class="field-body">
            <div class="control mt-2">
              <o-checkbox v-model="form.useInShipping"></o-checkbox>
            </div>
          </div>
        </div>

        <div class="field" v-if="item.formType === 'template'">
          <div class="field-label">
            <label class="label is-size-3">Name</label>
          </div>
          <div class="field-body">
            <div class="control">
              <input
                class="input"
                type="text"
                v-model.trim="form.qaqcName"
              />
            </div>
          </div>
        </div>

        <!-- Template ID -->
        <div class="field" v-if="item.formType === 'template'">
          <div class="field-label">
            <label class="label is-size-3">Form ID</label>
          </div>
          <div class="field-body">
            <div class="control">
              <input
                class="input"
                type="text"
                v-model.trim="form.formId"
              />
            </div>
          </div>
        </div>
        
          <div class="columns">
            <div class="column" v-if="config.showColor">
              <h3 class="is-size-3 has-text-black-bis has-text-weight-semibold line-height">
                Color <span class="has-text-danger">*</span>
              </h3>
               <tag-color-picker
                :color="form.color"
                :edit="true"
                @update:color="form.color = $event"
              />
            </div>
          </div>
          <div class="columns">
            <div class="column" v-if="config.showSubCategories">
              <h3 class="is-size-3 has-text-black-bis has-text-weight-semibold line-height">
                Subcategories
              </h3>
              <div
                  v-for="(subCat, idx) in form.subCategories"
                  :key="idx"
                  class="is-flex is-align-items-center mb-2"
                >
                  <input
                    class="input mr-2"
                    type="text"
                    v-model.trim="subCat.name"
                    :placeholder="`Subcategory ${idx + 1}`"
                  />
                  <button
                    class="button is-danger is-light is-small"
                    @click="removeSubCategory(idx)"
                  >
                    <i class="icon-removedelete"></i>
                  </button>
                </div>
                <button class="button is-light is-small mt-2" @click="addSubCategory">
                  <i class="icon-addnew mr-1"></i> Add Subcategory
                </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="modal-card-foot is-justify-content-flex-end is-align-items-center">
        <div class="column is-narrow">
          <div>
            <button class="button is-outlined mr-2" @click="cancel">Cancel</button>
            <button
              class="button has-background-black-bis has-text-white"
              :disabled="saveDisabled"
              @click="save"
            >
              {{ isEdit ? `Update ${config.label}` : `Save ${config.label}` }}
            </button>
          </div>
        </div>
      </footer>

      <o-loading :full-page="true" :active="isLoading" :can-cancel="false" />
    </div>
  </o-modal>
</template>

<script>
import { reactive, toRefs, computed, onBeforeMount } from 'vue';
import { cloneDeep, isEmpty, trim, some, uniq, map, get } from 'lodash';
import { useToast } from 'vue-toastification';
import { DialogProgrammatic } from '@/components/Dialog';
import TagColorPicker from '@/components/TagColorPicker.vue';

export default {
  name: 'ListItemForm',

  components: {
    'tag-color-picker': TagColorPicker,
  },

  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Object,
      required: true,
    },
  },

  emits: ['close', 'refresh-table'],

  setup(props, { emit }) {
    const toast = useToast();

   const data = reactive({
      isLoading: false,
      form: {
        name: '',
        color: '#43AAC7',
        subCategories: [],
        formId: '',
        templateName: '',
        templateId: '',
        qaqcName: '',
        useInShipping: false,
      },
    });
    const isEdit = computed(() => !!props.item?._id);

    const formTitle = computed(() => {
      if (props.item.formType === 'template') {
        return data.form.qaqcName || data.form.name || data.form.templateName || '';
      }
      return data.form.name || data.form.templateName || data.form.qaqcName || '';
    });
    const saveDisabled = computed(() => isEmpty(formTitle.value));

    const addSubCategory = () => data.form.subCategories.push({ name: '' });
    const removeSubCategory = (idx) => data.form.subCategories.splice(idx, 1);

    const validate = () => {
      let errMsg = null;
      if (props.item.formType === 'template' && (data.form.qaqcName.length < 3)) {
        errMsg = 'Form Name should be minimum of three characters';
      } else if (props.item.cardType === 'templateForm') {
        const templateName = trim(data.form.templateName);
        if (templateName.length < 3) {
          errMsg = 'Template Name should be minimum of three characters';
        } 
        const templateId = trim(data.form.templateId);
        if (!errMsg && (templateId.length < 4 || templateId.length > 16)) {
          errMsg = 'Template Id should be have a Minimum of 4 Characters and Maximum of 16 Characters';
        }
      } else if (isEmpty(formTitle.value)) {
        errMsg = `${props.config.label} Name cannot be empty`;
      } else if (formTitle.value.length < 3) {
        errMsg = `${props.config.label} name should be at least 3 characters long`;
      } else if (props.config.showSubCategories && some(data.form.subCategories, (s) => isEmpty(s.name))) {
        errMsg = 'Subcategory Name cannot be empty';
      } else if (
        props.config.showSubCategories
        && !isEmpty(data.form.subCategories)
        && some(data.form.subCategories, (s) => trim(s.name).length < 3)
      ) {
        errMsg = 'Subcategory name should be at least 3 characters long';
      } else if (props.config.showSubCategories) {
        const subcat = uniq(map(data.form.subCategories, (s) => s.name.toUpperCase()));
        if (!isEmpty(data.form.subCategories) && data.form.subCategories.length !== subcat.length) {
          errMsg = 'Subcategory names cannot be the same';
        }
      }

      if (errMsg) {
        DialogProgrammatic.confirm({ title: 'Error',message: errMsg, okButton: 'OK' });
        return false;
      }
      return true;
    };

    const save = async () => {
      if (!validate()) return;

      data.isLoading = true;
      try {
        const payload = {
          ...cloneDeep(props.item),
          name: formTitle.value,
          formName: formTitle.value,
          templateName: data.form.templateName || formTitle.value,
          templateId: data.form.templateId || props.item.templateId,
          formId: data.form.formId || props.item.formId,
          qaqcName: data.form.qaqcName || formTitle.value,
          useInShipping: !!data.form.useInShipping,
          ...(props.config.showSubCategories && { subCategories: data.form.subCategories }),
          ...(props.config.showColor && { color: data.form.color }),
        };

        if (isEdit.value) {
          await props.config.updateFn(payload);
          toast.success(`${props.config.label} updated successfully`);
        } else {
          const result = await props.config.saveFn(payload);
          if (result?.failedData?.length) {
            const errMsg = get(result, 'failedData[0]._errorMsg', `Error saving ${props.config.label}`);
            DialogProgrammatic.confirm({title: 'Error', message: errMsg, okButton: 'OK' });
            data.isLoading = false;
            return;
          }
          toast.success(`${props.config.label} added successfully`);
        }

        emit('refresh-table');
        emit('close');
      } catch (e) {
        toast.error(
          get(e, 'data.message') || get(e, 'data.msg') || e.message || `Error saving ${props.config.label}`,
        );
      }
      data.isLoading = false;
    };

    const cancel = () => emit('close');

    onBeforeMount(() => {
      if (!isEmpty(props.item)) {
        data.form.name = props.item.name || props.item.templateName || '';
        data.form.color = props.item.color || '#43AAC7';
        data.form.formId = props.item.formId || '';
        data.form.useInShipping = !!props.item.useInShipping;
        data.form.subCategories = cloneDeep(props.item.subCategories || []);

        if (props.item.cardType === 'templateForm') {
          data.form.templateName = props.item.templateName || props.item.name || '';
          data.form.templateId = props.item.templateId || '';
        } else {
          data.form.templateName = '';
          data.form.templateId = '';
        }

        if (props.item.formType === 'template') {
          data.form.qaqcName = props.item.formName || props.item.name || '';
        } else {
          data.form.qaqcName = '';
        }
      }
    });

    return {
      ...toRefs(data),
      isEdit,
      saveDisabled,
      addSubCategory,
      removeSubCategory,
      save,
      cancel,
    };
  },
};
</script>

<style scoped>
.modal-card-head {
  background-color: #fff !important;
  border-bottom: none !important;
}
.modal-card-foot {
  border-top: 1px solid #84888c;
  background-color: #fff !important;
}
:deep(.modal-card-body) {
  overflow: visible !important;
}
:deep(.modal-card) {
  overflow: visible !important;
}
</style>
